import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Loader from './Loader';
import Table from './Table';
import { COMMUNITY_ADMIN_PATH } from '../../AdminDashboards/adminTools';
import TokenTimeline from './TokenTimeline';

const LIMIT = 30;

const GET_USER_ACTIVITY = gql`
  query getUserActivity($userId: ID!, $limit: Int) {
    getUserActivity(userId: $userId, limit: $limit) {
      user {
        id
        email
        nickname
        createdAt
      }
      results {
        id
        surveyName
        createdAt
        completedAt
        complete
        campaignFailure
        deleted
        notCounted
        fromWebSurvey
        removalDetails {
          removalType
          removedAt
          removedByClientEmail
          removedFromQuestionId
        }
      }
      transactions {
        id
        createdAt
        newBalance
        savingGroupId
        tokens
        type
        shopItemName
        surveyName
      }
      purchases {
        id
        buyerAddressPlace
        buyerAddressPostCode
        buyerAddressProvince
        buyerFirstName
        buyerLastName
        buyerEmail
        buyerPhoneNumber
        shopItemName
        savingGroupMembers {
          id
          email
          nickname
          createdAt
        }
        validationReason
      }
    }
  }
`;

const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const formatDate = value =>
  value ? new Date(value).toLocaleDateString() : '-';

const orDash = value =>
  value === null || value === undefined || value === '' ? '-' : value;

// Flags that point at a problem are highlighted when set
function Flag({ value, warning }) {
  return (
    <span style={value && warning ? styles.flagWarning : undefined}>
      {value ? 'Yes' : 'No'}
    </span>
  );
}

const singlePage = rows => ({
  page: 0,
  setPage: () => {},
  total: rows.length,
  limit: rows.length || 1
});

function UserActivity({ match }) {
  const { userId } = match.params;

  const { data, loading, error } = useQuery(GET_USER_ACTIVITY, {
    fetchPolicy: 'network-only',
    variables: { userId, limit: LIMIT }
  });

  const activity = data && data.getUserActivity;
  const user = activity && activity.user;
  const results = (activity && activity.results) || [];
  const transactions = (activity && activity.transactions) || [];
  const purchases = (activity && activity.purchases) || [];

  const resultHeaders = [
    { key: 'surveyName', label: 'Survey name' },
    { key: 'createdAt', label: 'Created at' },
    { key: 'completedAt', label: 'Completed at' },
    { key: 'complete', label: 'Complete' },
    { key: 'campaignFailure', label: 'Campaign failure' },
    { key: 'deleted', label: 'Deleted' },
    { key: 'notCounted', label: 'Not counted' },
    { key: 'fromWebSurvey', label: 'From web survey' },
    { key: 'removalDetails', label: 'Removal details' }
  ];

  const renderResult = result => {
    const removal = result.removalDetails;

    return (
      <tr key={result.id}>
        <td style={styles.td}>{orDash(result.surveyName)}</td>
        <td style={styles.tdNowrap}>{formatDateTime(result.createdAt)}</td>
        <td style={styles.tdNowrap}>{formatDateTime(result.completedAt)}</td>
        <td style={styles.td}>
          <Flag value={result.complete} />
        </td>
        <td style={styles.td}>
          <Flag value={result.campaignFailure} warning />
        </td>
        <td style={styles.td}>
          <Flag value={result.deleted} warning />
        </td>
        <td style={styles.td}>
          <Flag value={result.notCounted} warning />
        </td>
        <td style={styles.td}>
          <Flag value={result.fromWebSurvey} />
        </td>
        <td style={styles.td}>
          {removal ? (
            <>
              <div>{orDash(removal.removalType)}</div>
              <div style={styles.muted}>
                {formatDateTime(removal.removedAt)}
                {removal.removedByClientEmail
                  ? ` by ${removal.removedByClientEmail}`
                  : ''}
              </div>
              {removal.removedFromQuestionId ? (
                <div style={styles.muted}>
                  question {removal.removedFromQuestionId}
                </div>
              ) : null}
            </>
          ) : (
            '-'
          )}
        </td>
      </tr>
    );
  };

  const transactionHeaders = [
    { key: 'createdAt', label: 'Created at' },
    { key: 'newBalance', label: 'New balance' },
    { key: 'savingGroupId', label: 'Saving group id' },
    { key: 'tokens', label: 'Tokens' },
    { key: 'type', label: 'Type' },
    { key: 'shopItemName', label: 'Shop item name' },
    { key: 'surveyName', label: 'Survey name' }
  ];

  const renderTransaction = transaction => (
    <tr key={transaction.id}>
      <td style={styles.tdNowrap}>{formatDateTime(transaction.createdAt)}</td>
      <td style={styles.tdNumber}>{orDash(transaction.newBalance)}</td>
      <td style={styles.tdMono}>{orDash(transaction.savingGroupId)}</td>
      <td style={styles.tdNumber}>{orDash(transaction.tokens)}</td>
      <td style={styles.td}>{orDash(transaction.type)}</td>
      <td style={styles.td}>{orDash(transaction.shopItemName)}</td>
      <td style={styles.td}>{orDash(transaction.surveyName)}</td>
    </tr>
  );

  const purchaseHeaders = [
    { key: 'address', label: 'Buyer address' },
    { key: 'buyerFirstName', label: 'First name' },
    { key: 'buyerLastName', label: 'Last name' },
    { key: 'buyerEmail', label: 'Buyer email' },
    { key: 'buyerPhoneNumber', label: 'Buyer phone' },
    { key: 'shopItemName', label: 'Shop item name' },
    { key: 'savingGroup', label: 'Saving group' },
    { key: 'validationReason', label: 'Validation reason' }
  ];

  const renderPurchase = purchase => {
    const address = [
      purchase.buyerAddressPlace,
      purchase.buyerAddressPostCode,
      purchase.buyerAddressProvince
    ]
      .filter(part => part)
      .join(', ');

    return (
      <tr key={purchase.id}>
        <td style={styles.td}>{orDash(address)}</td>
        <td style={styles.td}>{orDash(purchase.buyerFirstName)}</td>
        <td style={styles.td}>{orDash(purchase.buyerLastName)}</td>
        <td style={styles.td}>{orDash(purchase.buyerEmail)}</td>
        <td style={styles.tdNowrap}>{orDash(purchase.buyerPhoneNumber)}</td>
        <td style={styles.td}>{orDash(purchase.shopItemName)}</td>
        <td style={styles.td}>
          {purchase.savingGroupMembers && purchase.savingGroupMembers.length
            ? purchase.savingGroupMembers.map(member => (
                <div key={member.id} style={styles.member}>
                  <Link
                    to={`${COMMUNITY_ADMIN_PATH}/purchases-overview/${
                      member.id
                    }`}
                    style={styles.link}
                  >
                    {member.email || member.nickname || member.id}
                  </Link>
                  <span style={styles.muted}>
                    {' '}
                    created {formatDate(member.createdAt)}
                  </span>
                </div>
              ))
            : '-'}
        </td>
        <td style={styles.td}>{orDash(purchase.validationReason)}</td>
      </tr>
    );
  };

  return (
    <div style={styles.container}>
      <Link
        to={`${COMMUNITY_ADMIN_PATH}/purchases-overview`}
        style={styles.backLink}
      >
        &larr; Back to purchases overview
      </Link>

      {loading ? <Loader /> : null}
      {error ? <p style={styles.error}>{error.message}</p> : null}

      {user && !loading ? (
        <>
          <h1 style={styles.title}>{user.email || user.id}</h1>
          <p style={styles.intro}>
            {user.nickname ? `${user.nickname} · ` : ''}
            account created {formatDateTime(user.createdAt)}
          </p>

          <h2 style={styles.sectionTitle}>
            Results <span style={styles.count}>last {LIMIT}</span>
          </h2>
          {results.length ? (
            <div style={styles.scroll}>
              <Table
                headers={resultHeaders}
                data={results}
                renderRow={renderResult}
                pagination={singlePage(results)}
              />
            </div>
          ) : (
            <p style={styles.empty}>No results.</p>
          )}

          <h2 style={styles.sectionTitle}>
            Transactions <span style={styles.count}>last {LIMIT}</span>
          </h2>
          {transactions.length ? (
            <div style={styles.scroll}>
              <Table
                headers={transactionHeaders}
                data={transactions}
                renderRow={renderTransaction}
                pagination={singlePage(transactions)}
              />
            </div>
          ) : (
            <p style={styles.empty}>No transactions.</p>
          )}

          <h2 style={styles.sectionTitle}>
            Purchases <span style={styles.count}>last {LIMIT}</span>
          </h2>
          {purchases.length ? (
            <div style={styles.scroll}>
              <Table
                headers={purchaseHeaders}
                data={purchases}
                renderRow={renderPurchase}
                pagination={singlePage(purchases)}
              />
            </div>
          ) : (
            <p style={styles.empty}>No purchases.</p>
          )}

          <h2 style={styles.sectionTitle}>
            Token timeline{' '}
            <span style={styles.count}>
              the {transactions.length} transactions above
            </span>
          </h2>
          <TokenTimeline transactions={transactions} />
        </>
      ) : null}
    </div>
  );
}

const cell = {
  borderBottom: '1px solid #ddd',
  padding: '8px 10px',
  fontSize: '13px',
  verticalAlign: 'top'
};

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#007bff',
    textDecoration: 'none'
  },
  title: {
    marginBottom: '4px',
    wordBreak: 'break-all'
  },
  intro: {
    color: '#666',
    marginTop: 0
  },
  error: {
    color: '#c0392b'
  },
  empty: {
    color: '#666'
  },
  sectionTitle: {
    marginTop: '35px',
    marginBottom: 0,
    fontSize: '18px'
  },
  count: {
    fontSize: '13px',
    fontWeight: 'normal',
    color: '#666'
  },
  scroll: {
    overflowX: 'auto'
  },
  td: cell,
  tdNowrap: {
    ...cell,
    whiteSpace: 'nowrap'
  },
  tdNumber: {
    ...cell,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  tdMono: {
    ...cell,
    fontFamily: 'monospace',
    fontSize: '12px'
  },
  muted: {
    color: '#8b949e',
    fontSize: '12px'
  },
  flagWarning: {
    color: '#d73a49',
    fontWeight: 600
  },
  member: {
    whiteSpace: 'nowrap'
  },
  link: {
    color: '#007bff',
    textDecoration: 'none'
  }
};

export default UserActivity;
